import { ThresholdInfoMap, ThresholdRule, ThresholdUnit } from '../models/Threshold';
import { CardNode, CardNodeIndex } from './CardNode';

// CardList.ts ingests the api data and converts it
// to a useful first class object that we can use
// to render and manipulate the data in ThresholdDisplay.jsx
export class CardList {
  private readonly first: CardNode;
  private readonly middle: CardNode;
  private readonly last: CardNode;
  private readonly description: string;
  private readonly valuesFromApi: boolean;
  private readonly unit: ThresholdUnit;

  constructor(values: number[] | null, rule: ThresholdRule) {
    const thresholdInfo = ThresholdInfoMap[rule];

    this.valuesFromApi = !!values;
    this.description = thresholdInfo.getDescription();
    this.unit = thresholdInfo.unit;

    const defaultArray = thresholdInfo.defaultValues;

    // valueArray is equal to the values given from the API or the default values
    const valueArray = values ?? defaultArray;

    this.first = new CardNode(CardNodeIndex.FIRST, valueArray[0], valueArray[0] !== 0, this.unit, defaultArray[0]);
    this.middle = new CardNode(CardNodeIndex.SECOND, valueArray[1], valueArray[1] !== valueArray[0], this.unit, defaultArray[1]);
    this.last = new CardNode(CardNodeIndex.THIRD, valueArray[2], valueArray[2] !== valueArray[1], this.unit, defaultArray[2]);

    // double link each node to one another
    this.first.setNext(this.middle);
    this.middle.setPrevious(this.first);
    this.middle.setNext(this.last);
    this.last.setPrevious(this.middle);
  }

  cardsAreValid() {
    const cards = [this.first, this.middle, this.last];
    return cards.every((card) => card.isCardValid());
  }

  getFirst() {
    return this.first;
  }

  getMiddle() {
    return this.middle;
  }

  getLast() {
    return this.last;
  }

  getDescription() {
    return this.description;
  }

  areValuesFromApi() {
    return this.valuesFromApi;
  }

  // Returns the values of the cards in the format
  // required by the API. If the unit of the threshold is "bits" then
  // then medHighThreshold <= lowMedThreshold <= offLowThreshold and if the unit
  // of the threshold is "days" then offLowThreshold <= lowMedThreshold <= medHighThreshold
  getValuesInApiFormat() {
    return {
      medHighThreshold: this.unit === 'bits' ? this.first.getValue() : this.last.getValue(),
      lowMedThreshold: this.middle.getValue(),
      offLowThreshold: this.unit === 'bits' ? this.last.getValue() : this.first.getValue(),
    };
  }

  static blank(rule: ThresholdRule) {
    return new CardList([1, 2, 3], rule);
  }
}
